import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import NotificationsActiveRoundedIcon from "@mui/icons-material/NotificationsActiveRounded";
import NotificationsOffRoundedIcon from "@mui/icons-material/NotificationsOffRounded";
import PhoneIphoneRoundedIcon from "@mui/icons-material/PhoneIphoneRounded";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import {
  activatePushNotifications,
  deactivatePushNotifications,
  getPushStatus,
  sendPushTest,
  syncGrantedPushSubscription,
} from "../../../utils/pushNotifications";

const STATUS_COPY = {
  active: {
    title: "Alertas de pagamento ativos",
    description: "Você será avisado quando um pagamento for confirmado, mesmo com o painel fechado.",
  },
  inactive: {
    title: "Ativar notificações de pagamentos",
    description: "Receba um alerta na hora em que um Pix ou cartão for confirmado e abra direto em Vendas e pagamentos.",
  },
  blocked: {
    title: "Notificações bloqueadas",
    description: "Abra as configurações deste site no navegador, permita notificações e volte aqui.",
  },
  ios_install_required: {
    title: "Instale no iPhone para receber alertas",
    description: "No Safari, toque em Compartilhar, escolha Adicionar à Tela de Início e abra a Future Trade pelo novo ícone.",
  },
  unsupported: {
    title: "Notificações indisponíveis",
    description: "Este navegador não oferece notificações push. Tente usar Safari, Chrome, Edge ou Firefox atualizado.",
  },
};

function useAdminPushStatus() {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState("");
  const [feedback, setFeedback] = useState(null);

  const refreshStatus = useCallback(async () => {
    try {
      setStatus(await getPushStatus());
    } catch {
      setStatus({ status: "inactive", label: "Desativadas" });
    }
  }, []);

  useEffect(() => {
    refreshStatus();
    syncGrantedPushSubscription().catch(() => {});
    const handleChange = () => refreshStatus();
    window.addEventListener("futuretrade:push-status-changed", handleChange);
    return () => window.removeEventListener("futuretrade:push-status-changed", handleChange);
  }, [refreshStatus]);

  const activate = async () => {
    setBusy("activate");
    setFeedback(null);
    try {
      await activatePushNotifications();
      setFeedback({ severity: "success", text: "Notificações ativadas neste dispositivo." });
      await refreshStatus();
    } catch (error) {
      setFeedback({ severity: "error", text: error.message });
    } finally {
      setBusy("");
    }
  };

  const deactivate = async () => {
    setBusy("deactivate");
    setFeedback(null);
    try {
      await deactivatePushNotifications();
      setFeedback({ severity: "info", text: "Alertas desativados neste dispositivo." });
      await refreshStatus();
    } catch (error) {
      setFeedback({ severity: "error", text: error.message });
    } finally {
      setBusy("");
    }
  };

  const sendTest = async () => {
    setBusy("test");
    setFeedback(null);
    try {
      await sendPushTest();
      setFeedback({ severity: "success", text: "Teste enviado. A notificação deve aparecer em alguns segundos." });
    } catch (error) {
      setFeedback({ severity: "error", text: error.message });
    } finally {
      setBusy("");
    }
  };

  const toggle = async () => {
    if (status?.status === "active") {
      await deactivate();
      return;
    }
    await activate();
  };

  return { status, busy, feedback, setFeedback, activate, deactivate, sendTest, toggle };
}

function HeaderPushToggle() {
  const { status, busy, feedback, setFeedback, toggle } = useAdminPushStatus();

  if (!status) {
    return (
      <Button
        disabled
        size="small"
        startIcon={<CircularProgress size={14} />}
        sx={{
          minHeight: 40,
          height: 40,
          px: 1.25,
          borderRadius: "999px",
          textTransform: "none",
          fontWeight: 700,
          fontSize: "0.75rem",
          color: "text.secondary",
          border: "1px solid rgba(255,255,255,0.14)",
        }}
      >
        Notificações
      </Button>
    );
  }

  const active = status.status === "active";
  const blocked = status.status === "blocked";
  const label = busy
    ? "Atualizando..."
    : active
      ? "Pagamentos: ativas"
      : status.status === "ios_install_required"
        ? "Instalar no iPhone"
        : "Ativar notificações de pagamentos";

  return (
    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 0.6, minWidth: 0 }}>
      <Button
        type="button"
        onClick={toggle}
        disabled={Boolean(busy) || status.status === "unsupported"}
        sx={{
          minHeight: 40,
          height: 40,
          px: 1,
          pr: 1.4,
          borderRadius: "999px",
          textTransform: "none",
          fontWeight: 700,
          fontSize: { xs: "0.68rem", sm: "0.75rem" },
          gap: 1,
          color: active ? "#b8f5d1" : blocked ? "#ffc9c9" : "#d7f7ff",
          bgcolor: active ? "rgba(102,214,154,.12)" : blocked ? "rgba(255,100,100,.1)" : "rgba(85,223,247,.1)",
          border: active
            ? "1px solid rgba(102,214,154,.35)"
            : blocked
              ? "1px solid rgba(255,120,120,.35)"
              : "1px solid rgba(85,223,247,.35)",
          "&:hover": {
            transform: "none",
            boxShadow: "none",
            bgcolor: active ? "rgba(102,214,154,.18)" : blocked ? "rgba(255,100,100,.16)" : "rgba(85,223,247,.16)",
          },
        }}
      >
        <Box
          sx={{
            position: "relative",
            width: 42,
            height: 24,
            borderRadius: "999px",
            flexShrink: 0,
            bgcolor: active ? "rgba(102,214,154,.9)" : blocked ? "rgba(220,80,80,.75)" : "rgba(120,140,160,.55)",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              top: 2,
              left: active ? 20 : 2,
              width: 20,
              height: 20,
              borderRadius: "50%",
              bgcolor: "#fff",
              boxShadow: "0 4px 12px rgba(0,0,0,.35)",
              transition: "left .18s ease",
            }}
          />
        </Box>
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", lineHeight: 1.15, minWidth: 0 }}>
          <Typography sx={{ fontSize: "0.58rem", letterSpacing: "0.1em", textTransform: "uppercase", opacity: 0.7 }}>
            Notificações
          </Typography>
          <Typography sx={{ fontSize: "inherit", fontWeight: 800, whiteSpace: { xs: "normal", sm: "nowrap" } }}>
            {label}
          </Typography>
        </Box>
      </Button>
      {feedback && (
        <Alert
          severity={feedback.severity}
          onClose={() => setFeedback(null)}
          sx={{ maxWidth: 320, borderRadius: "10px", fontSize: "0.68rem", py: 0 }}
        >
          {feedback.text}
        </Alert>
      )}
    </Box>
  );
}

function AdminPushCard({ compact = false }) {
  const { status, busy, feedback, activate, deactivate, sendTest } = useAdminPushStatus();

  if (!status) return compact ? null : <CircularProgress size={24} />;

  const copy = STATUS_COPY[status.status] || STATUS_COPY.inactive;
  const canActivate = ["inactive", "ios_install_required"].includes(status.status);

  return (
    <Paper
      component="section"
      elevation={0}
      sx={{
        mx: compact ? { xs: 1.25, sm: 2.5 } : 0,
        mt: compact ? 1.25 : 0,
        p: compact ? { xs: 1.4, sm: 1.7 } : { xs: 2, sm: 2.5 },
        borderRadius: "16px",
        bgcolor: "rgba(22, 34, 56, 0.96)",
        border: "1px solid rgba(85, 223, 247, 0.22)",
        boxShadow: compact ? "none" : "0 16px 40px rgba(0,0,0,.2)",
      }}
    >
      <Box sx={{ display: { sm: "flex" }, alignItems: "center", justifyContent: "space-between", gap: 2 }}>
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.25, minWidth: 0 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              flexShrink: 0,
              display: "grid",
              placeItems: "center",
              borderRadius: "12px",
              color: status.status === "active" ? "#66d69a" : "#55dff7",
              bgcolor: status.status === "active" ? "rgba(102,214,154,.11)" : "rgba(85,223,247,.09)",
            }}
          >
            {status.status === "active" ? <NotificationsActiveRoundedIcon /> : <NotificationsOffRoundedIcon />}
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 0.8 }}>
              <Typography sx={{ fontSize: compact ? "0.86rem" : "1rem", fontWeight: 850 }}>
                {copy.title}
              </Typography>
              {status.status === "active" && (
                <Chip label="ATIVO" size="small" color="success" sx={{ height: 21, fontSize: "0.58rem", fontWeight: 900 }} />
              )}
            </Box>
            <Typography sx={{ mt: 0.35, maxWidth: 680, color: "text.secondary", fontSize: compact ? "0.69rem" : "0.78rem", lineHeight: 1.5 }}>
              {copy.description}
            </Typography>
          </Box>
        </Box>

        <Stack direction="row" spacing={0.8} sx={{ mt: { xs: 1.4, sm: 0 }, pl: { xs: 6.5, sm: 0 }, flexWrap: "wrap" }}>
          {status.status === "active" ? (
            <>
              <Button
                onClick={sendTest}
                disabled={Boolean(busy)}
                startIcon={busy === "test" ? <CircularProgress size={14} /> : <SendRoundedIcon />}
                variant="contained"
                size="small"
                sx={{ minHeight: 38, bgcolor: "#55dff7", color: "#07131f", fontSize: "0.7rem", "&:hover": { bgcolor: "#75e8ff", transform: "none" } }}
              >
                Enviar teste
              </Button>
              {!compact && (
                <Button onClick={deactivate} disabled={Boolean(busy)} variant="outlined" size="small" sx={{ minHeight: 38, fontSize: "0.7rem" }}>
                  Desativar aqui
                </Button>
              )}
            </>
          ) : (
            canActivate && (
              <Button
                onClick={activate}
                disabled={Boolean(busy)}
                startIcon={
                  busy === "activate" ? (
                    <CircularProgress size={14} />
                  ) : status.status === "ios_install_required" ? (
                    <PhoneIphoneRoundedIcon />
                  ) : (
                    <NotificationsActiveRoundedIcon />
                  )
                }
                variant="contained"
                size="small"
                sx={{ minHeight: 38, bgcolor: "#55dff7", color: "#07131f", fontSize: "0.7rem", "&:hover": { bgcolor: "#75e8ff", transform: "none" } }}
              >
                {status.status === "ios_install_required" ? "Ver como instalar" : "Ativar notificações de pagamentos"}
              </Button>
            )
          )}
        </Stack>
      </Box>

      {feedback && (
        <Alert severity={feedback.severity} sx={{ mt: 1.3, borderRadius: "11px", fontSize: "0.7rem" }}>
          {feedback.text}
        </Alert>
      )}
    </Paper>
  );
}

export default function AdminPushSetup({ compact = false, variant = "card" }) {
  if (variant === "header") {
    return <HeaderPushToggle />;
  }
  return <AdminPushCard compact={compact} />;
}
